import React, { useState } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { MemoryFilter } from "@/components/memory/MemoryFilter";
import { MemoryExporter } from "@/components/memory/MemoryExporter";
import { useThemeColor } from "@/hooks/useThemeColor";
import { MemoryModel, MediaModel, Contact, GroupModel } from "common247";
import { IFilteredEntityResultSet } from "@/services/memory-filter-service";

// Mock source dataset defined at module level for stable references
const SAMPLE_MEMORIES: MemoryModel[] = [
  {
    id: "mem-1",
    title: "Stockholm Archipelago Midsummer",
    description: "Sailing across the islands with family, dancing around the maypole.",
    date: "20-Jun-2026 to 24-Jun-2026",
    contactIds: ["c-alice", "c-bob"],
    groupIds: ["g-family"],
    media: [
      {
        id: "med-1",
        filename: "archipelago-sunset.jpg",
        contentType: "image/jpeg",
        favouriteRating: 5,
      } as MediaModel,
    ],
  } as any,
  {
    id: "mem-2",
    title: "Alpine Skiing Expedition",
    description: "Powder snow runs in the mountains with fellow winter sports enthusiasts.",
    date: "15-Dec-2026 to 18-Dec-2026",
    contactIds: ["c-bob"],
    groupIds: ["g-friends"],
    media: [
      {
        id: "med-2",
        filename: "skiing-downhill.mp4",
        contentType: "video/mp4",
        favouriteRating: 4,
      } as MediaModel,
    ],
  } as any,
  {
    id: "mem-3",
    title: "Heirloom Book Reading Evening",
    description: "Quiet evening discussing classical poetry and ancestral memories.",
    date: "05-Jan-2026",
    contactIds: ["c-alice"],
    groupIds: ["g-family"],
    media: [],
  } as any,
  {
    id: "mem-4",
    title: "Autumn Jazz & Acoustic Recording",
    description: "Studio jam session capturing live acoustic melodies.",
    date: "12-Oct-2026",
    contactIds: ["c-charlie"],
    groupIds: ["g-friends"],
    media: [
      {
        id: "med-3",
        filename: "jazz-solo.mp3",
        contentType: "audio/mpeg",
        favouriteRating: 3,
      } as MediaModel,
    ],
  } as any,
];

const SAMPLE_MEDIA: MediaModel[] = [
  {
    id: "med-1",
    filename: "archipelago-sunset.jpg",
    contentType: "image/jpeg",
    favouriteRating: 5,
  } as MediaModel,
  {
    id: "med-2",
    filename: "skiing-downhill.mp4",
    contentType: "video/mp4",
    favouriteRating: 4,
  } as MediaModel,
  {
    id: "med-3",
    filename: "jazz-solo.mp3",
    contentType: "audio/mpeg",
    favouriteRating: 3,
  } as MediaModel,
];

const SAMPLE_CONTACTS: Contact[] = [
  { id: "c-alice", name: "Alice Wonderland", email: "alice@example.com" } as any,
  { id: "c-bob", name: "Bob Builder", email: "bob@example.com" } as any,
  { id: "c-charlie", name: "Charlie Chaplin", email: "charlie@example.com" } as any,
];

const SAMPLE_GROUPS: GroupModel[] = [
  { id: "g-family", name: "Family Circle" } as any,
  { id: "g-friends", name: "Friends Crew" } as any,
];

const INITIAL_RESULTS: IFilteredEntityResultSet = {
  memories: SAMPLE_MEMORIES,
  media: SAMPLE_MEDIA,
  contacts: SAMPLE_CONTACTS,
  groups: SAMPLE_GROUPS,
  totalCounts: {
    memories: SAMPLE_MEMORIES.length,
    media: SAMPLE_MEDIA.length,
    contacts: SAMPLE_CONTACTS.length,
    groups: SAMPLE_GROUPS.length,
  },
};

/**
 * Interactive Design Workbench for MemoryFilter component.
 * Allows designers and testers to evaluate multi-dimensional filtering
 * across dates, text query, linked contacts, groups, media types, and ratings.
 */
export default function MemoryFilterDesignScreen(): React.JSX.Element {
  const tintColor = useThemeColor({}, "tint");
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const [filteredResults, setFilteredResults] = useState<IFilteredEntityResultSet>(INITIAL_RESULTS);

  return (
    <ThemedScreen
      title="MemoryFilter Design Sandbox"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 8 }}>
          ARCHIVAL HEIRLOOM COMPONENT WORKBENCH
        </ThemedText>

        <ThemedPanel padding={12} elevation="low" style={{ marginBottom: 12 }}>
          <View style={styles.headerRow}>
            <View style={[styles.iconBox, { backgroundColor: badgeBg }]}>
              <Ionicons name="filter" size={20} color={tintColor} />
            </View>
            <View style={{ flex: 1 }}>
              <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                MemoryFilter Component
              </ThemedText>
              <ThemedText style={{ fontSize: 12, color: textMuted }}>
                Cross-Entity Filter with PDF & Image Export Triggers
              </ThemedText>
            </View>
          </View>
          <ThemedText style={styles.description}>
            Interactive test harness for MemoryFilter. Filter across Date To/From, Contact associations,
            Group memberships, Media types (image/video/audio), and minimum ratings.
          </ThemedText>
        </ThemedPanel>

        {/* Live MemoryFilter Component Instance */}
        <MemoryFilter
          title="Filter Memories"
          allMemories={SAMPLE_MEMORIES}
          allMedia={SAMPLE_MEDIA}
          allContacts={SAMPLE_CONTACTS}
          allGroups={SAMPLE_GROUPS}
          onFilteredResultsChange={setFilteredResults}
        />

        {/* Dedicated MemoryExporter Controls */}
        <ThemedPanel padding={12} elevation="low" style={{ marginTop: 10 }}>
          <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <ThemedText type="defaultSemiBold" style={{ fontSize: 13 }}>
              Archival Exporter (MemoryExporter)
            </ThemedText>
            <MemoryExporter data={filteredResults} />
          </View>
          <ThemedText style={{ fontSize: 11, color: textMuted }}>
            Independent export controls for generating archival PDFs and image snapshots of filtered sets.
          </ThemedText>
        </ThemedPanel>

        {/* Filtered Results Inspector */}
        <ThemedPanel padding={12} elevation="low" style={styles.resultsPanel}>
          <ThemedText type="defaultSemiBold" style={{ fontSize: 14, marginBottom: 8 }}>
            Filtered Results Output ({filteredResults.memories.length} Memories)
          </ThemedText>

          <View style={styles.countsRow}>
            <View style={[styles.countBadge, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="memory" size={14} color={tintColor} style={{ marginRight: 4 }} />
              <ThemedText style={styles.countBadgeText}>
                {filteredResults.totalCounts.memories} Memories
              </ThemedText>
            </View>
            <View style={[styles.countBadge, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="media" size={14} color={tintColor} style={{ marginRight: 4 }} />
              <ThemedText style={styles.countBadgeText}>
                {filteredResults.totalCounts.media} Media
              </ThemedText>
            </View>
            <View style={[styles.countBadge, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="contact" size={14} color={tintColor} style={{ marginRight: 4 }} />
              <ThemedText style={styles.countBadgeText}>
                {filteredResults.totalCounts.contacts} Contacts
              </ThemedText>
            </View>
            <View style={[styles.countBadge, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="group" size={14} color={tintColor} style={{ marginRight: 4 }} />
              <ThemedText style={styles.countBadgeText}>
                {filteredResults.totalCounts.groups} Groups
              </ThemedText>
            </View>
          </View>

          {/* Render Memory Previews */}
          <View style={styles.cardsList}>
            {filteredResults.memories.map((m) => (
              <View key={m.id} style={[styles.memoryItem, { borderColor, backgroundColor: cardBg }]}>
                <View style={styles.memoryItemHeader}>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 14, flex: 1 }}>
                    {m.title}
                  </ThemedText>
                  <ThemedText style={{ fontSize: 11, color: textMuted }}>{m.date}</ThemedText>
                </View>
                <ThemedText style={{ fontSize: 12, color: textMuted, marginTop: 4 }}>
                  {m.description}
                </ThemedText>
              </View>
            ))}
            {filteredResults.memories.length === 0 && (
              <ThemedText style={{ fontStyle: "italic", color: textMuted, padding: 12, textAlign: "center" }}>
                No memories match current filter criteria.
              </ThemedText>
            )}
          </View>
        </ThemedPanel>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    paddingBottom: 32,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
  },
  iconBox: {
    width: 38,
    height: 38,
    borderRadius: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  description: {
    fontSize: 13,
    lineHeight: 18,
    opacity: 0.85,
  },
  resultsPanel: {
    marginTop: 10,
  },
  countsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  countBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderRadius: 0,
  },
  countBadgeText: {
    fontSize: 12,
    fontWeight: "600",
  },
  cardsList: {
    gap: 8,
  },
  memoryItem: {
    borderWidth: 1,
    padding: 10,
    borderRadius: 0,
  },
  memoryItemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
});
